import { apiClient, toQuery, saveBlob } from "./client";

/**
 * The portal user list.
 *
 * ⚠️ Active users ONLY by default, and that default is load-bearing: this one
 * endpoint backs all 21 user-selection surfaces (query key ["users"]), so an
 * archived person disappears from every picker without any of them knowing
 * archiving exists. Pass { includeInactive: true } only where archived people
 * are the subject — the Settings user list, and the archived-name lookup below.
 */
export async function fetchUsers({ includeInactive = false } = {}) {
  const qs = toQuery({ include_inactive: includeInactive ? "true" : "" });
  const res = await apiClient("get", `/users${qs ? `?${qs}` : ""}`);
  return res.data;
}

/**
 * Archived users only — a lookup table, never an option list.
 *
 * A picker whose stored value points at an archived person cannot find their
 * name in ["users"] (which excludes them, correctly), so it would have to label
 * the option with a raw uid. This supplies the name. Deliberately a separate
 * query key so nothing that renders options can accidentally map over it.
 */
/**
 * Create a PLACEHOLDER user — a row for someone who has not signed in yet.
 *
 * ⚠️ This does not create a login. Identity lives in Firebase, and a real
 * account row appears when the person signs in. What this creates is the row
 * that can hold their assignments in the meantime; `get_or_create_user` merges
 * it into their real account on first sign-in, matched on EMAIL — which is why
 * a typo in the email does not just mislabel the row, it strands the work on it.
 */
export async function createUser(payload) {
  const res = await apiClient("post", "/users", payload);
  return res.data;
}

export async function fetchArchivedUsers() {
  const all = await fetchUsers({ includeInactive: true });
  return all.filter((u) => !u.is_active);
}

export async function fetchUserProfile(userId) {
  const res = await apiClient("get", `/users/${userId}/profile`);
  return res.data;
}

/**
 * A person's visits + logged hours for one profile window (backs the profile's
 * pie chart). Own data needs no permission; anyone else's needs users.read.
 */
export async function fetchUserActivity(userId, range, includeUnapproved = false, custom = null) {
  // Hours are PM-approved only unless asked — see technician_activity_service.
  // `custom` = { start, end } ("yyyy-MM-dd", inclusive), read only when range === "custom".
  const qs = [`range=${encodeURIComponent(range)}`];
  if (includeUnapproved) qs.push("include_unapproved=true");
  if (range === "custom" && custom) qs.push(`start=${custom.start}`, `end=${custom.end}`);
  const res = await apiClient("get", `/users/${userId}/activity?${qs.join("&")}`);
  return res.data;
}

/**
 * The Work Summary cards for a CUSTOM date range (the five standard windows
 * ride along on fetchUserProfile). Same window as fetchUserActivity's custom.
 */
export async function fetchUserWindowStats(userId, start, end) {
  const res = await apiClient("get", `/users/${userId}/window-stats?start=${start}&end=${end}`);
  return res.data;
}

/**
 * The employee-review PDF. Only the chart SELECTIONS are sent — the server
 * computes every figure itself, so the document never rests on browser numbers.
 */
export async function downloadUserActivityReport(userId, userName, selection) {
  const res = await apiClient("post", `/users/${userId}/activity-report`, selection, {
    responseType: "blob",
  });
  const slug = (userName || "employee").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  saveBlob(res.data, `employee-report-${slug}-${selection.range.replace(/_/g, "-")}.pdf`, "application/pdf");
}

// Editing your own profile needs no permission; editing anyone else's needs
// users.write. Only fields the server lists as self-editable are accepted.
export async function updateUserProfile(userId, payload) {
  const res = await apiClient("patch", `/users/${userId}`, payload);
  return res.data;
}

export async function updateUserRole(userId, role) {
  const res = await apiClient("patch", `/users/${userId}/role`, { role });
  return res.data;
}

/** What this user is holding — shown before archiving them. system_admin only. */
export async function fetchUserImpact(userId) {
  const res = await apiClient("get", `/users/${userId}/impact`);
  return res.data;
}

/**
 * Archive: the person is rejected at the API, their name stays attached to
 * everything they were on, and those attachments stay manually editable.
 * Refuses a system_admin (demote first) and anyone still clocked in.
 */
export async function archiveUser(userId) {
  const res = await apiClient("post", `/users/${userId}/archive`);
  return res.data;
}

export async function restoreUser(userId) {
  const res = await apiClient("post", `/users/${userId}/restore`);
  return res.data;
}

/**
 * A person's default Labor / Overtime / Travel-hours cost rates.
 * ⚠️ system_admin ONLY, read and write — the server 403s anyone else, so never
 * call this for a non-admin viewer. Rates are never part of a user payload.
 */
export async function fetchUserPayRates(userId) {
  const res = await apiClient("get", `/users/${userId}/pay-rates`);
  return res.data;
}

export async function updateUserPayRates(userId, payload) {
  const res = await apiClient("put", `/users/${userId}/pay-rates`, payload);
  return res.data;
}

// ⚠️ system_admin ONLY (GET/PUT 403 anyone else, even on their own record).
// Date of birth, sex and home address — never part of a user payload. Pay rates
// are the section's fourth member and have their own calls above.
export async function fetchUserSensitiveInfo(userId) {
  const res = await apiClient("get", `/users/${userId}/sensitive-info`);
  return res.data;
}

export async function updateUserSensitiveInfo(userId, payload) {
  const res = await apiClient("put", `/users/${userId}/sensitive-info`, payload);
  return res.data;
}

// ---- Profile photo (Oct 14 2026): yourself, or a system admin. ----
/** → the updated user (with `photo_url`). */
export async function uploadUserPhoto(userId, file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient("POST", `/users/${userId}/photo`, formData);
  return res.data;
}

export async function removeUserPhoto(userId) {
  const res = await apiClient("delete", `/users/${userId}/photo`);
  return res.data;
}

// ---- Emergency contact: read = self / system admin / HR / their manager;
// write = self / system admin / HR. 404 for anyone else (render nothing). ----
export async function fetchEmergencyContact(userId) {
  const res = await apiClient("get", `/users/${userId}/emergency-contact`);
  return res.data;
}

export async function saveEmergencyContact(userId, payload) {
  const res = await apiClient("put", `/users/${userId}/emergency-contact`, payload);
  return res.data;
}

/** Everyone's name, title, photo and manager — readable by every role. */
export async function fetchOrgChart() {
  const res = await apiClient("get", "/users/org-chart");
  return res.data;
}
